import { Flex, Spacing, Text } from '@billy/design-system'
import { type ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useForm } from '../../../../../../hooks/useForm'
import { useRegisterInvoicingSettingsForm } from '../../../../hooks/useRegisterInvoicingSettingsForm'
import type {
  InvoicingSettingsFormSaveOptions,
  InvoicingSettingsFormSaveResult,
} from '../../../../types/invoicingSettingsFormSave'
import type { ComplianceSettingsFormSchema } from '../../types/complianceSettingsFormSchema'
import { getCompliancePayload } from '../../utils/getCompliancePayload'
import { ComplianceSettingsFormFields } from './elements/ComplianceSettingsFormFields'
import { usePredefinedComplianceSettingsFormValues } from './hooks/usePredefinedComplianceSettingsFormValues'
import { getComplianceSettingsDefaultValues } from './utils/getComplianceSettingsDefaultValues'
import { getComplianceSettingsFormSchema } from './utils/getComplianceSettingsFormSchema'

export const ComplianceSettingsForm = (): ReactElement => {
  const { t } = useTranslation()
  const { hasFailedToLoad, isLoading, predefinedComplianceSettingsFormValues, refetch } =
    usePredefinedComplianceSettingsFormValues()
  const { Form, formState, getValues, reset, trigger } = useForm<ComplianceSettingsFormSchema>({
    defaultValues: getComplianceSettingsDefaultValues(),
    validationSchema: getComplianceSettingsFormSchema(),
  })

  const { isDirty } = formState
  const hasLoadedSettings = !isLoading && !hasFailedToLoad

  const handleFormReset = useCallback(() => {
    reset(getValues())
  }, [getValues, reset])

  const handleSave = useCallback(
    async ({ updateSettings }: InvoicingSettingsFormSaveOptions): Promise<InvoicingSettingsFormSaveResult> => {
      if (!hasLoadedSettings) {
        throw new Error('Compliance settings are not loaded')
      }

      const isValid = await trigger()

      if (!isValid) {
        return 'invalid'
      }

      // No data invalidation needed: the settings writer seeds its cache from the PUT response.
      await updateSettings({ values: getCompliancePayload(getValues()) })

      return 'saved'
    },
    [getValues, hasLoadedSettings, trigger],
  )

  useRegisterInvoicingSettingsForm({ isDirty, resetForm: handleFormReset, save: handleSave })

  // Replace the empty defaults with server values once the settings document has loaded.
  // A failed fetch leaves those defaults in place and the fields unmounted, so Save cannot send them.
  // Skip this while the user has unsaved edits, so a refetch cannot wipe them.
  useEffect(() => {
    if (!hasLoadedSettings || isDirty) {
      return
    }

    reset(predefinedComplianceSettingsFormValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDirty is a guard, not a dependency: re-running when the form turns clean after save would reset over the values the host just kept
  }, [hasLoadedSettings, predefinedComplianceSettingsFormValues, reset])

  return (
    <Flex flexDirection="column" gap={Spacing.S24}>
      <Text variant="h2">{t('invoicing_settings.compliance')}</Text>
      <Form>
        <ComplianceSettingsFormFields hasFailedToLoad={hasFailedToLoad} isLoading={isLoading} onRetry={refetch} />
      </Form>
    </Flex>
  )
}
